/* The pack sheet (snippets/st-pack-sheet.liquid): quick add from a product card.
   - "Choose a pack" on a card that carries its variants (snippets/st-product-tile.liquid with quick,
     marked data-st-quick) opens the sheet instead of the product page. The link stays a link: a
     modifier-key or middle click, or a page without this script, opens the product page as before.
   - Every price shown was printed by the money filter into the card's data, so the sheet formats no
     money and makes no request until Add to cart.
   - The sheet is a native modal <dialog> (the page behind is inert), with Tab kept inside it,
     Escape and a click outside to close, the page's scroll locked through Horizon's lockScroll, and
     focus returned to the card's button.
   - Add to cart posts the pack to /cart/add.js with the cart drawer's sections, as
     assets/st-quick-add.js does, and announces a CartLinesUpdateEvent from inside the dialog: Horizon's
     cart drawer waits for this dialog to close, then opens on the redrawn cart with the new line.
     A preorder pack carries the same Preorder property as the product page's (assets/st-pack.js).
   - A cart line's edit button (snippets/st-cart-line.liquid, part 'opts') opens the sheet in the
     cart, with the line in its data: the sheet opens on the line's pack and options, its button reads
     "Update cart", and choosing adds the new pack with the line's quantity, then removes the old line
     (add first, so a failed add changes nothing). In the drawer the change asks for the cart sections
     and announces a CartLinesUpdateEvent, so the drawer redraws and stays open, and focus goes to
     the new line's edit button; on the cart page the page reloads, as the pack advice does.
   The choice logic is exported, so scripts/qa/st-filter.test.mjs runs it in node. */
import { lockScroll, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const fill = (tpl, map) => Object.entries(map).reduce((s, [k, v]) => s.split(k).join(String(v)), tpl || '');

/** Each value of option n, in the order the variants first use it. */
export function optionValues(data, n) {
  const seen = [];
  data.variants.forEach((v) => {
    if (!seen.includes(v.o[n])) seen.push(v.o[n]);
  });
  return seen;
}

/** The packs sold with the other options as chosen (choice holds a value per option), smallest first. */
export function packsFor(data, choice) {
  return data.variants
    .filter((v) => v.o.every((value, n) => n === data.pack || value === choice[n]))
    .sort((a, b) => a.q - b.q);
}

/** The pack to open on: the size wanted when it can be bought, else the nearest that can (the smaller on a tie), else null. */
export function pickPack(packs, want) {
  const open = packs.filter((p) => p.a);
  if (!open.length) return null;
  return open.reduce((best, p) => {
    const d = Math.abs(p.q - want);
    const bd = Math.abs(best.q - want);
    return d < bd || (d === bd && p.q < best.q) ? p : best;
  });
}

/** Per cent less a unit than in the smallest pack (base), from the per-unit prices in cents. */
export function saving(pack, base) {
  if (!pack || !base || pack === base || !base.c) return 0;
  return Math.max(0, Math.round((1 - pack.c / base.c) * 100));
}

const DEFAULT_PACK = 10;

/** The pack size to open on: the cart line's own when the sheet edits one, else the pack of 10. */
export function startSize(data) {
  const current = data?.line && data.variants?.find((v) => v.id === data.line.id);
  return current ? current.q : DEFAULT_PACK;
}

/** Tell assistive tech that each quick card's button opens a dialog (only once the script can open it). */
const mark = () => document.querySelectorAll('a[data-st-quick]').forEach((a) => a.setAttribute('aria-haspopup', 'dialog'));

if (typeof customElements !== 'undefined' && !customElements.get('st-pack-sheet')) {
  customElements.define(
    'st-pack-sheet',
    class extends HTMLElement {
      connectedCallback() {
        if (this.dialog) return;
        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('[data-st-form]');
        if (!this.dialog || !this.form) return;
        // Cards that arrive after the page loads are marked too: the product page's recommendations
        // are fetched and morphed into the section that holds this sheet.
        const section = this.closest('.shopify-section');
        if (section && !this.closest('cart-items-component')) new MutationObserver(mark).observe(section, { childList: true, subtree: true });
        // Every way out (the close button, Escape, a click outside, after adding) ends here. The
        // scroll position is put back as Horizon's own dialogs do (assets/dialog.js).
        this.dialog.addEventListener('close', () => {
          unlockScroll(this.dialog);
          scrollTo({ top: this.pageTop || 0, behavior: 'instant' });
          this.#restore();
        });
        // A click on the backdrop lands on the dialog itself: the panel fills the dialog and scrolls
        // inside it, so no click on the sheet or its scrollbar does.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        this.dialog.addEventListener('keydown', (event) => this.#trap(event));
        this.querySelector('[data-st-close]')?.addEventListener('click', () => this.close());
        this.form.addEventListener('change', (event) => {
          if (event.target.name === 'id') this.#choose(Number(event.target.value));
        });
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.#addToCart();
        });
        this.querySelector('[data-st-opts]')?.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-st-val]');
          if (!chip || chip.getAttribute('aria-pressed') === 'true') return;
          this.choice[Number(chip.dataset.stOpt)] = chip.dataset.stVal;
          chip.parentElement.querySelectorAll('[data-st-val]').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
          this.#packs(this.pack?.q ?? DEFAULT_PACK);
        });
      }

      /** Fill the sheet with one product's data and open it; trigger gets focus back on close. */
      open(data, trigger) {
        this.data = data;
        this.trigger = trigger;
        this.line = data.line || null;
        this.choice = [...(data.selected || data.variants[0]?.o || [])];
        this.pack = null;
        this.querySelector('[data-st-title]').textContent = data.title;
        const best = this.querySelector('[data-st-best]');
        if (best) {
          best.textContent = data.best || '';
          best.hidden = !data.best;
        }
        const ph = this.querySelector('[data-st-img]');
        if (ph) {
          ph.replaceChildren();
          if (data.img) {
            const img = document.createElement('img');
            img.src = data.img;
            img.alt = '';
            img.width = 56;
            img.height = 56;
            ph.append(img);
          }
        }
        const more = this.querySelector('[data-st-details]');
        if (more) more.href = data.url;
        const error = this.querySelector('[data-st-error]');
        if (error) error.hidden = true;
        this.#options();
        this.#packs(startSize(data));
        this.pageTop = getScrollTop();
        lockScroll(this.dialog);
        this.dialog.showModal();
        // Focus the chosen pack: a screen reader hears the dialog's name, then the pack and its price.
        const start = this.form.querySelector('input[name="id"]:checked') || this.querySelector('[data-st-close]');
        start?.focus();
      }

      close() {
        if (!this.dialog?.open) return;
        this.dialog.close();
        this.#restore();
      }

      // Back to the card's button, unless focus has already moved on (the cart drawer opening).
      #restore() {
        const active = document.activeElement;
        const lost = !active || active === document.body || this.dialog.contains(active);
        if (lost && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      }

      // Tab from the last control goes to the first, Shift+Tab from the first to the last.
      #trap(event) {
        if (event.key !== 'Tab') return;
        const first = this.querySelector('[data-st-close]');
        const last = this.querySelector('[data-st-details]');
        if (!first || !last) return;
        if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      }

      // A row of chips for each option other than the pack that has more than one value.
      #options() {
        const box = this.querySelector('[data-st-opts]');
        if (!box) return;
        const groups = [];
        (this.data.options || []).forEach((name, n) => {
          if (n === this.data.pack) return;
          const values = optionValues(this.data, n);
          if (values.length < 2) return;
          const set = document.createElement('fieldset');
          set.className = 'st-sheet__opt';
          const legend = document.createElement('legend');
          legend.className = 'st-sheet__lg';
          legend.textContent = name;
          const row = document.createElement('div');
          row.className = 'st-chips';
          values.forEach((value) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'st-chip';
            chip.dataset.stOpt = String(n);
            chip.dataset.stVal = value;
            chip.setAttribute('aria-pressed', String(value === this.choice[n]));
            chip.textContent = value;
            row.append(chip);
          });
          set.append(legend, row);
          groups.push(set);
        });
        box.replaceChildren(...groups);
        box.hidden = !groups.length;
      }

      // The pack rows for the options chosen; keeps the size chosen when it is sold in the new option.
      #packs(want) {
        const d = this.data;
        const list = this.querySelector('[data-st-pack-list]');
        const packs = packsFor(d, this.choice);
        const base = packs[0];
        const rows = packs.map((p) => {
          const row = document.createElement('label');
          row.className = 'st-sheet__pack';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = 'id';
          input.value = String(p.id);
          input.disabled = !p.a;
          const q = document.createElement('span');
          q.className = 'st-sheet__q';
          q.textContent = String(p.o[d.pack] || p.q).toLowerCase();
          const price = document.createElement('span');
          price.className = 'st-sheet__p';
          price.textContent = p.p;
          const meta = document.createElement('span');
          meta.className = 'st-sheet__m';
          const off = saving(p, base);
          if (!p.a) meta.textContent = this.dataset.soldOut || '';
          else if (p.pre) meta.textContent = this.dataset.ships || '';
          else if (off > 0) meta.textContent = fill(d.noun ? this.dataset.save : this.dataset.saveEach, { __X__: off, __U__: d.noun });
          const unit = document.createElement('span');
          unit.className = 'st-sheet__u';
          unit.textContent = fill(this.dataset.each, { __P__: p.u });
          row.append(input, q, price, meta, unit);
          return row;
        });
        list.replaceChildren(...rows);
        this.pack = packs.find((p) => p.q === want && p.a) || pickPack(packs, want);
        const checked = this.pack && list.querySelector(`input[value="${this.pack.id}"]`);
        if (checked) checked.checked = true;
        this.#button();
      }

      #choose(id) {
        this.pack = packsFor(this.data, this.choice).find((p) => p.id === id) || this.pack;
        this.#button();
      }

      // "Add to cart · $78.95", "Preorder · ..." for a pack that ships when back in stock, or Sold out.
      #button() {
        const button = this.querySelector('[data-st-add]');
        const label = this.querySelector('[data-st-add-label]');
        if (!button || !label) return;
        const p = this.pack;
        button.disabled = !p;
        if (!p) label.textContent = this.dataset.soldOut || '';
        // Editing a line: the price is one pack's, so it is shown only when the line holds one.
        else if (this.line) label.textContent = this.line.quantity === 1 ? fill(this.dataset.update, { __P__: p.p }) : this.dataset.updatePlain || '';
        else label.textContent = fill(p.pre ? this.dataset.preorder : this.dataset.add, { __P__: p.p });
      }

      async #addToCart() {
        const p = this.pack;
        if (!p || this.busy) return;
        if (this.line) return this.#replace(p);
        const button = this.querySelector('[data-st-add]');
        const error = this.querySelector('[data-st-error]');
        if (error) error.hidden = true;
        this.busy = true;
        button?.setAttribute('aria-busy', 'true');
        const root = window.Shopify?.routes?.root || '/';
        const json = (r) => {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        };
        try {
          const { CartLinesUpdateEvent } = await import('@shopify/events');
          const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((c) => c.dataset.sectionId);
          const item = {
            id: p.id,
            quantity: 1,
            ...(p.pre && this.dataset.preorderProp ? { properties: { [this.dataset.preorderProp]: this.dataset.ships || '' } } : {}),
          };
          const added = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [item],
              ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}),
            }),
          }).then(json);
          const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then(json);
          const deferred = CartLinesUpdateEvent.createPromise();
          // From inside the open dialog, so the drawer opens once it has closed (assets/cart-drawer.js).
          this.form.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: [{ merchandiseId: String(p.id), quantity: 1 }],
              promise: deferred.promise,
            })
          );
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { sections: added.sections, items: cart.items, itemCount: cart.item_count, source: 'st-pack-sheet', didError: false },
          });
          this.close();
        } catch (e) {
          if (error) {
            error.textContent = this.dataset.error || '';
            error.hidden = false;
          }
        } finally {
          this.busy = false;
          button?.removeAttribute('aria-busy');
        }
      }

      // Swap the cart line for the pack chosen: add it with the line's quantity, then remove the line.
      async #replace(p) {
        const line = this.line;
        if (p.id === line.id) {
          this.close();
          return;
        }
        const button = this.querySelector('[data-st-add]');
        const error = this.querySelector('[data-st-error]');
        if (error) error.hidden = true;
        this.busy = true;
        button?.setAttribute('aria-busy', 'true');
        const root = window.Shopify?.routes?.root || '/';
        const post = (url, body) =>
          fetch(`${root}${url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          }).then((r) => {
            if (!r.ok) throw new Error(String(r.status));
            return r.json();
          });
        const drawer = () => [...document.querySelectorAll('dialog[open]')].find((d) => d.querySelector('cart-drawer-component'));
        const inDrawer = !!this.closest('cart-drawer-component');
        let added = false;
        try {
          await post('cart/add.js', {
            items: [
              {
                id: p.id,
                quantity: line.quantity,
                ...(p.pre && this.dataset.preorderProp ? { properties: { [this.dataset.preorderProp]: this.dataset.ships || '' } } : {}),
              },
            ],
          });
          added = true;
          const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((c) => c.dataset.sectionId);
          const changed = await post('cart/change.js', {
            id: line.key,
            quantity: 0,
            ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}),
          });
          if (!inDrawer || !changed.sections) {
            window.location.reload();
            return;
          }
          this.close();
          const { CartLinesUpdateEvent } = await import('@shopify/events');
          const deferred = CartLinesUpdateEvent.createPromise();
          this.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'update',
              context: 'cart',
              lines: [{ id: line.key, quantity: 0 }],
              promise: deferred.promise,
            })
          );
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(changed),
            detail: { sections: changed.sections, items: changed.items, itemCount: changed.item_count, source: 'st-pack-sheet', didError: false },
          });
          // The redraw lands on its own schedule: wait until the old line has gone, then put focus on the
          // new line's edit button, whose name says what the line now holds.
          const key = changed.items?.find((item) => item.variant_id === p.id)?.key;
          const old = () => drawer()?.querySelector(`tr[data-key="${CSS.escape(line.key)}"]`);
          for (let waited = 0; old() && waited < 3000; waited += 100) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
          await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
          const next = key && drawer()?.querySelector(`tr[data-key="${CSS.escape(key)}"] [data-st-edit]`);
          next?.focus();
        } catch (e) {
          if (error) {
            error.textContent = (added ? this.dataset.editPartial : this.dataset.error) || '';
            error.hidden = false;
          }
          if (added) window.setTimeout(() => window.location.reload(), 4000);
        } finally {
          this.busy = false;
          button?.removeAttribute('aria-busy');
        }
      }
    }
  );

  // One listener for every card on the page. Each card's data is read when its sheet opens.
  mark();
  document.addEventListener('shopify:section:load', mark);
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[data-st-quick]');
    if (!link) return;
    const source = link.closest('.st-ptile')?.querySelector('script[data-st-quick-data]');
    // Never the cart's own sheet: in the drawer it sits inside a closed dialog.
    const sheet =
      link.closest('.shopify-section')?.querySelector('st-pack-sheet') || document.querySelector('st-pack-sheet:not(cart-items-component st-pack-sheet)');
    if (!source || !sheet || typeof sheet.open !== 'function') return;
    let data;
    try {
      data = JSON.parse(source.textContent);
    } catch (e) {
      return;
    }
    if (!data?.variants?.length) return;
    event.preventDefault();
    sheet.open(data, link);
  });

  // A cart line's edit button opens the sheet rendered under the same cart's lines.
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('button[data-st-edit]');
    if (!button) return;
    const source = button.parentElement?.querySelector('script[data-st-quick-data]');
    const sheet = button.closest('cart-items-component')?.querySelector('st-pack-sheet');
    if (!source || !sheet || typeof sheet.open !== 'function') return;
    let data;
    try {
      data = JSON.parse(source.textContent);
    } catch (e) {
      return;
    }
    if (!data?.variants?.length || !data.line) return;
    sheet.open(data, button);
  });

  // A line of one pack shows "Remove" under its options and hides the quantity cell (Matt,
  // 2026-10-21); the link presses Horizon's own remove button in that cell, so removal, its animation
  // and the redraw stay Horizon's.
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('button[data-st-remove]');
    if (!link) return;
    link.closest('.cart-items__table-row, tr')?.querySelector('.cart-items__remove')?.click();
  });
}
